import { useState } from 'react';
import { Tab } from '@headlessui/react';
import {
  BookOpenIcon,
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

export default function HowToUse() {
  const [searchQuery, setSearchQuery] = useState('');

  const [categories] = useState([
    {
      id: 'acronyms',
      name: 'Acronyms',
      icon: BookOpenIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium mb-4">Common Acronyms</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium">CCET</h4>
                    <p className="text-sm text-gray-600">Cyber Crisis Executive Team</p>
                  </div>
                  <div>
                    <h4 className="font-medium">CCMT</h4>
                    <p className="text-sm text-gray-600">Cyber Crisis Management Team</p>
                  </div>
                  <div>
                    <h4 className="font-medium">CCRT</h4>
                    <p className="text-sm text-gray-600">Cyber Crisis Response Team</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium">CSIRT</h4>
                    <p className="text-sm text-gray-600">Computer Security Incident Response Team</p>
                  </div>
                  <div>
                    <h4 className="font-medium">CCMP</h4>
                    <p className="text-sm text-gray-600">Cyber Crisis Management Plan</p>
                  </div>
                  <div>
                    <h4 className="font-medium">IRL</h4>
                    <p className="text-sm text-gray-600">In Real Life</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'faq',
      name: 'FAQ',
      icon: QuestionMarkCircleIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium mb-4">Frequently Asked Questions</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium">What is RESILENT™ Crisis Management?</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    A comprehensive platform designed to help organizations prepare for, respond to, and manage cyber security crises effectively.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium">How do I get started?</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Begin by completing the onboarding training modules and setting up your organization's structure in the Organization section.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium">How can I customize the response plan?</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Navigate to the Response Plan section where you can modify workflows, roles, and procedures according to your organization's needs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'search',
      name: 'Search',
      icon: MagnifyingGlassIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="max-w-xl">
              <div className="relative">
                <input
                  type="text"
                  className="block w-full rounded-md border-gray-300 pl-10 pr-3 py-2 text-sm placeholder-gray-500 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
              </div>
            </div>
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-500">Quick Links</h3>
              <ul className="mt-2 space-y-2">
                <li>
                  <a href="#" className="text-sm text-indigo-600 hover:text-indigo-900">Getting Started Guide</a>
                </li>
                <li>
                  <a href="#" className="text-sm text-indigo-600 hover:text-indigo-900">Response Plan Templates</a>
                </li>
                <li>
                  <a href="#" className="text-sm text-indigo-600 hover:text-indigo-900">Training Materials</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">How to Use</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}